/**
 * These are all of the different field types that have descriptions based on special database queries.
 * This is analogous to the au.com.finsyn.acuritywebserver.core.forms.generic.FieldTypeEnum java class on the server.
 * We only include the types where we want to display their descriptions on the main table.
 */
enum FieldTypeEnum {
	ACCOUNT = 'ACCOUNT',
	CONT_ACCOUNT = 'CONT_ACCOUNT',
	SUB_ACCOUNT = 'SUB_ACCOUNT',
	SUB_SUB_ACCOUNT = 'SUB_SUB_ACCOUNT',
	UNIT_CODE = 'UNIT_CODE',
	TRANS_REF = 'TRANS_REF',
	TRANS_TYPE = 'TRANS_TYPE',
	CATEGORY = 'CATEGORY',
	PENDING_CATEGORY = 'PENDING_CATEGORY',
	TIERED_INT_TABLE = 'TIERED_INT_TABLE',
	BATCH_CODE = 'BATCH_CODE',
	QTE_BATCH_CODE = 'QTE_BATCH_CODE',
	PAYEE_CODE = 'PAYEE_CODE',
	PAYROLL_CODE = 'PAYROLL_CODE',
	INVESTMENT_MANAGER_CODE = 'INVESTMENT_MANAGER_CODE',
	CLIENT = 'CLIENT',
	ADVISER = 'ADVISER',
	ADVISER_NAME = 'ADVISER_NAME',
	FEE_DEFINITION = 'FEE_DEFINITION',
	INTEREST_TYPE = 'INTEREST_TYPE',
}

/**
 * This is analogous to the properties and methods of the au.com.finsyn.acuritywebserver.core.forms.generic.FieldTypeEnum
 */
class FieldType {
	constructor(
		name: FieldTypeEnum,
		fieldNameContainsList: string[],
		fieldNameNotContainsList: string[] = [],
	) {
		this.name = name;
		this.fieldNameContainsList = fieldNameContainsList;
		this.fieldNameNotContainsList = fieldNameNotContainsList;
	}

	public readonly name: FieldTypeEnum;

	private readonly fieldNameContainsList: readonly string[];
	private readonly fieldNameNotContainsList: readonly string[];

	public matches(fieldName: string): boolean {
		return (
			this.fieldNameContainsList.some((fieldNameContains) =>
				fieldName.includes(fieldNameContains),
			) &&
			this.fieldNameNotContainsList.every(
				(fieldNameContains) => !fieldName.includes(fieldNameContains),
			)
		);
	}
}

/**
 * Uncomment out field types that require column descriptions.
 * Server code in FieldDescriptionProviderImpl.java will need updating too.
 * Server code needs to match this as types that are not mentioned here but are sent
 * with code and descriptions will be treated as regular code and descriptions fields,
 * for example by the filter multi-select.
 */
export const fieldTypes: readonly FieldType[] = [
	new FieldType(
		FieldTypeEnum.ACCOUNT,
		['z_Account', 'y_Account'],
		[
			'f_Coy_Account',
			'z_Accounts_Payable',
			'z_Account_Type',
			'z_Accounting_Tran',
			'z_Account_Phase',
		],
	),
	new FieldType(FieldTypeEnum.CONT_ACCOUNT, ['z_Cont_Acct']),
	new FieldType(FieldTypeEnum.SUB_ACCOUNT, ['z_Sub_Account', 'y_Sub_Acct']),
	new FieldType(FieldTypeEnum.SUB_SUB_ACCOUNT, [
		'z_Sub_Sub_Account',
		'y_Sub_Sub_Acct',
	]),
	new FieldType(FieldTypeEnum.UNIT_CODE, ['z_Unit_Code']),
	new FieldType(FieldTypeEnum.TRANS_REF, ['l_Trans_Ref']),
	new FieldType(
		FieldTypeEnum.TRANS_TYPE,
		['z_Transaction', 'z_Tran_Type'],
		['TDz_Tran_Type'],
	),
	new FieldType(FieldTypeEnum.CATEGORY, ['z_Category', 'z_New_Category']),
	new FieldType(FieldTypeEnum.PENDING_CATEGORY, ['z_Pending_Category']),
	new FieldType(FieldTypeEnum.TIERED_INT_TABLE, ['z_Tiered_Int_Table']),
	new FieldType(FieldTypeEnum.BATCH_CODE, ['z_Batch_Code']),
	new FieldType(FieldTypeEnum.QTE_BATCH_CODE, ['z_Qte_Batch_Code']),
	new FieldType(
		FieldTypeEnum.PAYEE_CODE,
		['z_Payee'],
		[
			'z_PayeeAustBusNo',
			'z_PayeeBSB',
			'z_PayeeBankActName',
			'z_PayeeBankActNo',
			'z_PayeeProdID',
			'z_Payee_Name',
		],
	),
	new FieldType(
		FieldTypeEnum.PAYROLL_CODE,
		['z_Payroll'],
		['z_Payroll_No', 'z_Payroll_Name', 'z_Payroll_Number'],
	),
	new FieldType(FieldTypeEnum.INVESTMENT_MANAGER_CODE, [
		'z_Manager',
		'z_InvMgr',
		'z_Inv_Mgr',
	]),
	new FieldType(
		FieldTypeEnum.CLIENT,
		['z_Client'],
		['z_Client_Code', 'z_Client_Ref_No', 'CFz_Client', 'SSz_Client'],
	),
	new FieldType(
		FieldTypeEnum.ADVISER,
		['z_Adviser', 'z_Company_Code', 'z_Master', 'BHz_Number'],
		['BDz_Adviser'],
	),
	new FieldType(FieldTypeEnum.ADVISER_NAME, ['z_Adviser_Code']),
	new FieldType(FieldTypeEnum.FEE_DEFINITION, ['z_Comm_Type']),
	new FieldType(FieldTypeEnum.INTEREST_TYPE, ['z_Interest_Type']),
];
